<template>
<v-card :tile='tile' :flat='flat' >
  <slot name='title' ></slot>
  <v-img v-if='preview' height="200" :src='preview' class='text-right' >

    <v-chip v-if='location.is_virtual === true' color='amber' class='ma-3' label small >
      <v-icon small left>
        mdi-web
      </v-icon>
      Virtual</v-chip>
    <template v-else >
      <v-badge right bordered overlap color="blue-grey" class='mt-3 mr-3' icon="mdi-open-in-new" >
        <v-btn
          :href='gmapsURL'
          target="_blank"
          fab small dark color='blue' class='elevation-10' >
          <v-icon>mdi-google-maps</v-icon>
        </v-btn>
      </v-badge>
      <v-badge right bordered overlap
        v-if='threeWordsURL'
        color="blue-grey" class='mt-3 mr-3'
        icon="mdi-open-in-new" >
        <v-btn small fab dark
          :href='threeWordsURL'
          target='_blank'
          class='elevation-10' color='red' >
          ///
        </v-btn>
      </v-badge>
    </template>
  </v-img>
  <v-divider/>
  <v-card-title class='subtitle-1 overline' >
    {{location.name}}
    <v-spacer />
    <v-chip small>{{latlng}}
      <v-icon small right >mdi-pencil</v-icon>
    </v-chip>
  </v-card-title>
  <v-card-text>
    <template v-if='!location.is_virtual' >
      <div v-if='location.address' >{{location.address}}</div>
      <div v-if="location.preferred_map_url" class="caption" >
        <v-icon x-small class="mr-1"
          @click="copyText(location.preferred_map_url)" >mdi-content-copy</v-icon>
        <a :href="location.preferred_map_url" target="_blank" >
          {{location.preferred_map_url}} <v-icon x-small>mdi-open-in-new</v-icon>
        </a>
      </div>
      <div v-else-if="location.map_link" class="caption" >
        <v-icon x-small class="mr-1"
          @click="copyText(location.map_link)" >mdi-content-copy</v-icon>
        <a :href="location.map_link" target="_blank" >
          {{location.map_link}} <v-icon x-small>mdi-open-in-new</v-icon>
        </a>
      </div>
      <div v-if="location.threeWordsURL" class="caption" >
        <a :href="location.threeWordsURL" target="_blank" >
          {{location.three_words}} <v-icon x-small>mdi-open-in-new</v-icon>
        </a>
      </div>
    </template>
    <p v-else-if="location.is_virtual && location.url" >
      <strong>The meeting is online</strong><br/>
      Meeting URL: <a :href='location.url' target='_blank' >{{location.url}}</a>
    </p>
    <v-list v-if='listitems.length > 0' dense >
      <template v-for='li in listitems' >
        <generic-list-item
          :item='li'
          :key='li.id'
          :with-edit='false' />
      </template>
    </v-list>
    <p v-if='location.notes'>
      <v-subheader class='pl-0 overline' >Extra information once you arrive</v-subheader>
      {{location.notes}}
    </p>
    <v-alert v-else-if="withTips" text tile dense
      type="info" class="caption" border="left" >
      You can add notes to give your clients extra information to help them find you after they arrive at the map address.
    </v-alert>
  </v-card-text>
  <slot name="card-actions" ></slot>
  <v-tooltip top>
    <template v-slot:activator="{ on, attrs }">
      <v-footer v-if='threeWordsURL && !location.is_online' >
        <v-btn text x-small :href='threeWordsURL' target='_blank' v-on='on' v-bind="attrs" >
          /// {{location.three_words}}
        </v-btn>
        <v-spacer />
        <v-btn x-small icon :href='threeWordsURL' target="_blank" v-on='on' v-bind="attrs" >
          <v-icon class='ml-2' small style='text-decoration:none;' >mdi-open-in-new</v-icon>
        </v-btn>
      </v-footer>
    </template>
    <span>
      what3words gives every 3m square in the world a unique address of 3 words.<br/>
      Click the link to navigate to {{location.name}}.<br/>
    </span>
  </v-tooltip>
  <!-- <pre v-text="location" /> -->
</v-card>
</template>
<script>
import GenericListItem from '@/components/listitems/GenericListItem.vue'

export default {
  name: 'LocationCard',
  components: {GenericListItem},
  props: {
    value: { type: Object, required: true },
    tile: { type: Boolean, default: true },
    flat: { type: Boolean, default: false },
    withTips: { type: Boolean, default: false }
  },
  computed: {
    location () {
      return this.value
    },
    threeWordsURL () {
      if (this.location && this.location.three_words) {
        return 'https://what3words.com/' + this.location.three_words
      }
      return null
    },
    listitems () {
      let li = []
      if (this.location.website) {
        li.push({ title: this.location.website, id: 'url', subtitle: 'Website', icon: 'mdi-web' })
      }
      if (this.location.phone) {
        li.push({ title: this.location.phone, id: 'phone', subtitle: 'Contact number', icon: 'mdi-phone' })
      }
      if (this.location.email) {
        li.push({ title: this.location.email, id: 'email', subtitle: 'Email', icon: 'mdi-email' })
      }
      return li
    },
    latlng () {
      if (this.location.latitude && this.location.longitude) {
        let lat = this.location.latitude.toString().substring(0,9)
        let lng = this.location.longitude.toString().substring(0,8)
        // let lat = this.location.latitude
        // let lng = this.location.longitude
        return `${lat},${lng}`
      }
      return null
    },
    gmapsURL () {
      if (this.location.preferred_map_url) {
        return this.location.preferred_map_url
      }
      let base = 'https://www.google.com/maps/search/?api=1'
      let query = this.location.address
      if (this.latlng) {
        query = `query=${this.latlng}`
      }
      if (this.location.place_id) {
        query += '&query_place_id=' + this.location.place_id
      }
      return `${base}&${query}`
    },
    meetingURL () {
      if (this.location.is_virtual === true) {
        return this.location.zoom_join_url
      }
      return null
    },
    preview () {
      if (this.location) {
        if (this.location.is_virtual === true) {
          return 'https://images.unsplash.com/photo-1480694313141-fce5e697ee25?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
        }
        if (this.location && this.location.latitude && this.location.longitude) {
          let lat = this.location.latitude
          let lng = this.location.longitude
          let lnglat = `${lng},${lat}`
          let token = 'pk.eyJ1IjoiYXBwb2ludG1lbnRndXJ1IiwiYSI6ImNsNndtZm1hcjJmeGczY29hbml0MGJhbGcifQ.pD__elCKqeJchH_wLdzJKA'
          return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-s+555555(${lnglat})/${lnglat},12,0,45/600x600@2x?access_token=${token}`
          // return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=10&size=280x120&scale=4&markers=size:tiny|color:blue|${lat},${lng}&key=AIzaSyDBLqBJhup-u2afKcqqonWzGC-vv_7YpnY`
        }
      }
      return null
    }
  }
}
</script>
